import { useEffect, useMemo, useState } from 'react';
import type { ContactWithOrganization, Tag } from '@epasu/core';
import {
  CAMPAIGN_MEMBER_STATE_LABELS,
  FOLLOWUP_STATE_LABELS,
  type CampaignMemberState,
} from '@epasu/core/constants';
import { UserPlus } from 'lucide-react';
import { crm, type CampaignMemberRow, type ContactRow } from '@/lib/crm-client';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CAMPAIGN_REJECT_LABEL } from '@/lib/campaign-reject-reason';

interface Props {
  contacts: ContactRow[];
}

const STATE_VARIANTE: Record<CampaignMemberState, NonNullable<BadgeProps['variant']>> = {
  offen: 'neutral',
  wird_versendet: 'warning',
  versendet: 'success',
  uebersprungen: 'neutral',
  entfernt: 'neutral',
  fehlgeschlagen: 'danger',
};

/**
 * Der Kampagnen-Reiter der Firmenakte.
 *
 * Zeigt je Ansprechpartner, in welchen Kampagnen er steckt und wie es um
 * seine Nachfass-Uhr steht. Steht eine Uhr auf „kalt“, ist das die Stelle,
 * an der entschieden wird, wer als Nächstes dran ist — `campaigns.nextContacts`
 * schlägt weitere Ansprechpartner derselben Firma vor, gewählt wird von Hand.
 *
 * Es gibt keinen gebündelten Endpunkt für „Kampagnen-Mitgliedschaft je
 * Kontakt“ — anders als Tags oder Lead-Listen kann ein Kontakt in mehreren
 * Kampagnen stecken. Geladen wird deshalb je Kampagne einmal, nicht je
 * Kontakt: die Anzahl der Kampagnen ist der begrenzende Faktor.
 */
export function CampaignsTab({ contacts }: Props) {
  const contactIds = useMemo(() => contacts.map((c) => c.id), [contacts]);
  const schluessel = contactIds.join(',');

  const [tags, setTags] = useState<Record<string, Tag[]>>({});
  const [mitgliedschaften, setMitgliedschaften] = useState<Map<string, CampaignMemberRow[]> | null>(
    null,
  );
  const [kampagnenName, setKampagnenName] = useState<Map<string, string>>(new Map());
  const [fehler, setFehler] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [naechster, setNaechster] = useState<CampaignMemberRow | null>(null);

  useEffect(() => {
    if (!schluessel) return;
    void crm.tags.forContacts(schluessel.split(',')).then(setTags);
  }, [schluessel]);

  useEffect(() => {
    if (!schluessel) {
      setMitgliedschaften(new Map());
      return;
    }
    let abgebrochen = false;
    const idSet = new Set(schluessel.split(','));
    void (async () => {
      try {
        const kampagnen = await crm.campaigns.list();
        const alleMitglieder = await Promise.all(kampagnen.map((k) => crm.campaigns.members(k.id)));
        if (abgebrochen) return;
        const namen = new Map(kampagnen.map((k) => [k.id, k.name]));
        const karte = new Map<string, CampaignMemberRow[]>();
        for (const liste of alleMitglieder) {
          for (const m of liste) {
            if (!idSet.has(m.contactId)) continue;
            karte.set(m.contactId, [...(karte.get(m.contactId) ?? []), m]);
          }
        }
        setKampagnenName(namen);
        setMitgliedschaften(karte);
      } catch (err) {
        setFehler(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [schluessel, refreshKey]);

  if (contacts.length === 0) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        Noch kein Ansprechpartner hinterlegt — dieser Reiter füllt sich, sobald einer da ist.
      </Card>
    );
  }

  return (
    <>
      <Card className="divide-y">
        {contacts.map((c) => {
          const mitglieder = mitgliedschaften?.get(c.id) ?? [];
          return (
            <div key={c.id} className="p-4">
              <div className="flex flex-wrap items-baseline gap-2">
                <p className="font-medium">{c.displayName}</p>
                {c.role && <span className="text-xs text-muted-foreground">{c.role}</span>}
              </div>
              {(tags[c.id]?.length ?? 0) > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {tags[c.id]!.map((t) => (
                    <Badge key={t.id} variant="neutral">
                      {t.label}
                    </Badge>
                  ))}
                </div>
              )}

              <div className="mt-2 space-y-1.5">
                {mitgliedschaften === null ? (
                  <p className="text-xs text-muted-foreground">Lade …</p>
                ) : mitglieder.length === 0 ? (
                  <p className="text-xs text-muted-foreground">In keiner Kampagne.</p>
                ) : (
                  mitglieder.map((m) => (
                    <div key={m.id} className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted-foreground">
                        {kampagnenName.get(m.campaignId) ?? '—'}
                      </span>
                      <Badge variant={STATE_VARIANTE[m.state]}>
                        {CAMPAIGN_MEMBER_STATE_LABELS[m.state]}
                      </Badge>
                      {m.followupState && (
                        <Badge variant={m.followupState === 'kalt' ? 'neutral' : 'info'}>
                          {FOLLOWUP_STATE_LABELS[m.followupState]}
                        </Badge>
                      )}
                      {m.followupState === 'kalt' && (
                        <Button variant="outline" size="sm" onClick={() => setNaechster(m)}>
                          <UserPlus className="mr-1.5 h-4 w-4" />
                          Nächsten Ansprechpartner aufnehmen
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </Card>

      {fehler && <p className="mt-3 text-sm text-danger">{fehler}</p>}

      {naechster && (
        <NaechsterAnsprechpartnerDialog
          mitglied={naechster}
          kampagneName={kampagnenName.get(naechster.campaignId) ?? 'die Kampagne'}
          onClose={() => setNaechster(null)}
          onDone={() => {
            setNaechster(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </>
  );
}

/**
 * Wer als Nächstes dran ist — vorgeschlagen, aber von Hand gewählt.
 *
 * `campaigns.nextContacts` sortiert vor, entscheidet aber nicht: bei zwei
 * bis vier Ansprechpartnern einer Schule ist eine automatische Wahl nur eine
 * Wahl, die niemand gesehen hat.
 */
function NaechsterAnsprechpartnerDialog({
  mitglied,
  kampagneName,
  onClose,
  onDone,
}: {
  mitglied: CampaignMemberRow;
  kampagneName: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [kandidaten, setKandidaten] = useState<ContactWithOrganization[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    void crm.campaigns.nextContacts(mitglied.id).then(setKandidaten, (err: unknown) =>
      setFehler(err instanceof Error ? err.message : 'Laden fehlgeschlagen'),
    );
  }, [mitglied.id]);

  const aufnehmen = async (contactId: string) => {
    setBusyId(contactId);
    setFehler(null);
    try {
      const bericht = await crm.campaigns.addMembers(mitglied.campaignId, [contactId]);
      if (bericht.added === 0) {
        const grund = bericht.rejected[0]?.reason;
        setFehler(
          grund
            ? `Konnte nicht aufgenommen werden: ${CAMPAIGN_REJECT_LABEL[grund]}.`
            : 'Konnte nicht aufgenommen werden.',
        );
        setBusyId(null);
        return;
      }
      onDone();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Aufnehmen fehlgeschlagen');
      setBusyId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Nächsten Ansprechpartner aufnehmen"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md space-y-3 rounded-lg bg-card p-6 shadow-xl">
        <h2 className="text-lg font-semibold">Nächsten Ansprechpartner aufnehmen</h2>
        <p className="text-sm text-muted-foreground">Für {kampagneName}.</p>

        {!kandidaten ? (
          <p className="text-sm text-muted-foreground">Lade Vorschläge …</p>
        ) : kandidaten.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Keine weiteren Ansprechpartner dieser Firma kommen in Frage.
          </p>
        ) : (
          <ul className="divide-y">
            {kandidaten.map((k) => (
              <li key={k.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{k.displayName}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {[k.role, k.email].filter(Boolean).join(' · ') || '—'}
                  </p>
                </div>
                <Button
                  size="sm"
                  disabled={busyId === k.id}
                  onClick={() => void aufnehmen(k.id)}
                >
                  Aufnehmen
                </Button>
              </li>
            ))}
          </ul>
        )}

        {fehler && <p className="text-sm text-danger">{fehler}</p>}

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Schließen
          </Button>
        </div>
      </div>
    </div>
  );
}
